// src/pages/CompleteProfile.js
//
// Pass 88.3 §6 — reliable profile completion. Canonical STAFF (operations and/or teaching entitlement) are
// NEVER forced through the legacy student age/country form — they go straight to their workspace. Genuine
// students complete name/age/country, and the save AWAITS the Firestore write AND refreshes the canonical
// AuthContext BEFORE navigating, so there is no redirect loop / stale state. Busy + error UX; no blank screen.
import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { canEnterOrganizationWorkspace, canEnterInstructorWorkspace } from '../utils/platformAccess';
import { resolveDashboardHome } from '../routes';

function CompleteProfile() {
  const { currentUser, userData, refreshUserData } = useAuth() || {};
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [country, setCountry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // STAFF BYPASS: a canonical ops/teaching member must not see the student form — send them to their workspace.
  const isStaff = canEnterOrganizationWorkspace(userData || {}) || canEnterInstructorWorkspace(userData || {});
  useEffect(() => {
    if (isStaff) navigate(resolveDashboardHome(userData || {}), { replace: true });
  }, [isStaff, userData, navigate]);

  useEffect(() => {
    const dn = userData?.name || currentUser?.displayName;
    if (dn) setName(dn);
  }, [userData?.name, currentUser?.displayName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const uid = currentUser?.uid;
    if (!uid) { setError('You are signed out. Please sign in again.'); return; }
    setBusy(true); setError('');
    try {
      await setDoc(doc(db, 'users', uid), {
        name: name.trim(), age, country, email: currentUser.email, role: 'student',
      }, { merge: true });
      // Refresh canonical AuthContext so routing sees the completed profile BEFORE we navigate (no loop).
      if (typeof refreshUserData === 'function') await refreshUserData();
      navigate(resolveDashboardHome({ ...(userData || {}), name: name.trim(), age, country, role: 'student' }), { replace: true });
    } catch (err) {
      console.error('CompleteProfile save failed:', err);
      setError('Could not save your profile right now. Please try again.');
      setBusy(false);
    }
  };

  if (isStaff) return <div className="max-w-md mx-auto mt-10 text-sm text-slate-600">Loading your workspace…</div>;

  return (
    <div className="max-w-md mx-auto mt-10">
      <h2 className="text-xl font-semibold mb-4">Complete Your Profile</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <input name="name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required className="p-2 border rounded" />
        <input name="age" placeholder="Age" value={age} onChange={(e) => setAge(e.target.value)} required className="p-2 border rounded" />
        <input name="country" placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} required className="p-2 border rounded" />
        {error ? <div data-testid="complete-profile-error" role="alert" className="text-sm text-rose-600">{error}</div> : null}
        <button type="submit" disabled={busy} data-testid="complete-profile-save" className="bg-blue-600 text-white p-2 rounded disabled:opacity-60">
          {busy ? 'Saving…' : 'Save Profile'}
        </button>
      </form>
    </div>
  );
}

export default CompleteProfile;
